import { Multilingual } from 'interfaces/offer.interface';
import { Media } from 'model';
import * as fetch from 'utils/fetch';

const BASE_URL = `${process.env.basePath}/api/manfredite/file`;

/**
 * Gets a file from current logged in Manfredite
 *
 * @param id - identifier for the required file
 * @returns the Media file
 */
export async function getFile(id: number): Promise<Media> {
  const res = await fetch.get<{
    data: Media;
  }>(`${BASE_URL}/${id}`);

  return res.data;
}

/**
 * Gets a file download URL from current logged in Manfredite
 *
 * @param id - identifier for the required file
 * @returns the Media file download URL
 *
 * @deprecated use ServiceManfrediteMedia.getFileDownloadLink instead
 */
export async function getFileDownloadLink(id: number): Promise<string> {
  const res = await fetch.get<{
    data: string;
  }>(`${BASE_URL}/${id}/download`);

  return res.data;
}

export async function downloadManfrediteMedia(media: Media): Promise<void> {
  const downloadLink = await getFileDownloadLink(media.id);
  const link = document.createElement('a');

  link.href = downloadLink;
  link.download = media.filename || 'unknown-file';

  link.click();
}

export type AllowedManfrediteFileType = 'cv' | 'mac' | 'image';

/**
 * Uploads a file which will be owned by current logged in Manfredite
 *
 * @param file - the file to upload
 * @returns the Media file
 *
 * @deprecated use ServiceManfrediteMedia.uploadImage instead
 */
export async function uploadManfrediteFile(
  file: File,
  type: AllowedManfrediteFileType
): Promise<Media> {
  return uploadFile({
    type,
    title: file.name,
    alt: {
      ES: file.name
    },
    file: file
  });
}

/**
 * Uploads a file which will be owned by current logged in Manfredite
 *
 * @param params - the file and file info to upload
 * @returns the Media file
 */
export async function uploadFile(params: {
  type: AllowedManfrediteFileType;
  title?: string;
  alt?: Multilingual;
  file?: Blob;
}): Promise<Media> {
  const uploadMediaParams = new FormData();

  uploadMediaParams.append('attached', params.file ?? '');
  uploadMediaParams.append('title', params.title ?? '');
  uploadMediaParams.append('alt', JSON.stringify(params.alt ?? ''));

  const data = await fetch.post<
    FormData,
    {
      data: Media;
    }
  >(`${BASE_URL}/${params.type}`, uploadMediaParams, {
    headers: {}
  });

  return data.data;
}

/**
 * Removes a file owned by the current logged in Manfredite
 *
 * @param id - the file to be removed
 * @returns nothing
 *
 * @deprecated use ServiceManfrediteMedia.deleteMedia instead
 */
export async function deleteFile(id: number): Promise<void> {
  return fetch._delete(`${BASE_URL}/${id}`);
}

export const MediaService = {
  deleteFile,
  uploadFile,
  uploadManfrediteFile
};
